/**
 * Telegram sign-in through our bot (ADR-0046): one path for sign-up and sign-in (the first visit makes the account).
 *
 *   click → the bot opens on the request (phone: t.me opens the app; computer: tg:// opens Telegram Desktop, with a QR
 *   code of the t.me link for a phone) → the person taps "Confirm" there → this page, polling, signs in by itself.
 *
 * A request is prepared as soon as the button shows, so the click opens Telegram inside the gesture itself (iOS opens
 * the app from a universal link only then; Chrome launches tg:// only then). The request the person is confirming is
 * kept in sessionStorage: a page that was reloaded or restored after the trip to the app picks the wait up again.
 * Polling pauses while the tab is hidden and runs at once when it comes back.
 *
 * Without a bot on the stand (GET /auth/telegram → configured: false) the button stays visible but disabled, with the
 * reason in its tooltip.
 */
import type { LoginResult, TelegramConfig, TelegramPollResult, TelegramStart } from "@prop/contracts";
import { Button, cn } from "@prop/ui";
import { useQuery } from "@tanstack/react-query";
import { CircleAlert, LoaderCircle } from "lucide-react";
import QRCode from "qrcode";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useAuthT, useLang } from "../../i18n";
import { ApiError, get, post } from "../../lib/api";

type Purpose = "sign_in" | "step_up";
type Req = TelegramStart & { purpose: Purpose };
/** What a confirmed request ends in: a sign-in answer, or (step-up) the request to hand to POST /auth/step-up. */
export type TelegramDone = { kind: "signed_in"; result: LoginResult } | { kind: "confirmed"; id: string; secret: string };

const POLL_MS = 1500;
/** A prepared request with less life than this is replaced before it is opened. */
const FRESH_MS = 90_000;
const STORE = "prop.tg.request";

function isPhone(): boolean {
  const ua = navigator.userAgent;
  return /Android|iPhone|iPad|iPod|Mobile/i.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
}

const fresh = (r: Req | null): r is Req => !!r && Date.parse(r.expiresAt) - Date.now() > FRESH_MS;

function stored(purpose: Purpose): Req | null {
  try {
    const r = JSON.parse(sessionStorage.getItem(STORE) ?? "null") as Req | null;
    return r && r.purpose === purpose && Date.parse(r.expiresAt) > Date.now() ? r : null;
  } catch {
    return null;
  }
}
function store(r: Req | null): void {
  try {
    if (r) sessionStorage.setItem(STORE, JSON.stringify(r));
    else sessionStorage.removeItem(STORE);
  } catch {
    // storage blocked: the wait still works, only a reload loses it
  }
}

/** Phone: the t.me link (the app, or Telegram's page with an "open" button). Computer: Telegram Desktop. */
function openTelegram(r: Req, phone: boolean): void {
  window.location.href = phone ? r.botUrl : r.appUrl;
}

function useTelegramConfig(): TelegramConfig | null {
  const q = useQuery({ queryKey: ["auth", "telegram"], queryFn: () => get<TelegramConfig>("/auth/telegram"), staleTime: Number.POSITIVE_INFINITY });
  return q.data ?? null;
}

type Phase = { name: "idle" } | { name: "opening" } | { name: "waiting"; req: Req } | { name: "expired" } | { name: "error"; message: string };

/** A transient failure (network, server restart): keep polling. Anything else is the answer. */
const transient = (err: unknown) => err instanceof ApiError && (err.code === "NETWORK" || err.status >= 500 || err.status === 429);

function useTelegramFlow(purpose: Purpose, enabled: boolean, onDone: (d: TelegramDone) => Promise<unknown>) {
  const t = useAuthT();
  const { lang } = useLang();
  const [phase, setPhase] = useState<Phase>(() => {
    const r = typeof window === "undefined" ? null : stored(purpose);
    return r ? { name: "waiting", req: r } : { name: "idle" };
  });
  const prepared = useRef<Req | null>(null);
  const preparing = useRef<Promise<Req> | null>(null);
  const doneRef = useRef(onDone);
  doneRef.current = onDone;

  const start = useCallback(async (): Promise<Req> => {
    preparing.current ??= post<TelegramStart>("/auth/telegram/start", { locale: lang, purpose })
      .then((s) => ({ ...s, purpose }))
      .finally(() => {
        preparing.current = null;
      });
    return preparing.current;
  }, [lang, purpose]);

  const prepare = useCallback(() => {
    if (!enabled || fresh(prepared.current)) return;
    start()
      .then((r) => {
        prepared.current = r;
      })
      .catch(() => undefined);
  }, [enabled, start]);

  useEffect(prepare, [prepare]);

  const wait = (r: Req) => {
    store(r);
    setPhase({ name: "waiting", req: r });
  };

  /** The click: open Telegram right inside it when a request is ready, else after starting one. */
  const begin = async () => {
    if (!enabled || phase.name === "opening") return;
    const phone = isPhone();
    const ready = fresh(prepared.current) ? prepared.current : null;
    prepared.current = null;
    if (ready) {
      wait(ready);
      openTelegram(ready, phone);
      return;
    }
    setPhase({ name: "opening" });
    try {
      const r = await start();
      wait(r);
      openTelegram(r, phone);
    } catch (err) {
      setPhase({ name: "error", message: err instanceof ApiError && err.message ? err.message : t("telegram.unavailable") });
    }
  };

  const reset = useCallback(() => {
    store(null);
    setPhase({ name: "idle" });
    prepare();
  }, [prepare]);

  const req = phase.name === "waiting" ? phase.req : null;
  useEffect(() => {
    if (!req) return;
    let stopped = false;
    let inFlight = false;
    const stop = (next: Phase) => {
      stopped = true;
      store(null);
      setPhase(next);
    };
    const tick = async () => {
      if (stopped || inFlight || document.visibilityState !== "visible") return;
      inFlight = true;
      try {
        const r = await post<TelegramPollResult>("/auth/telegram/poll", { id: req.id, secret: req.secret });
        if (r.status === "pending") return;
        if (r.status === "expired") {
          if (!stopped) stop({ name: "expired" });
          return;
        }
        // Confirmed and (sign-in) already taken on the server: deliver it even if "Cancel" was pressed meanwhile —
        // the session cookie is set, the page must not pretend otherwise.
        if (stopped && r.status === "confirmed") return;
        stopped = true;
        store(null);
        await doneRef.current(r.status === "confirmed" ? { kind: "confirmed", id: req.id, secret: req.secret } : { kind: "signed_in", result: r });
      } catch (err) {
        if (transient(err) && !stopped) return;
        stop({ name: "error", message: err instanceof ApiError && err.message ? err.message : t("telegram.unavailable") });
      } finally {
        inFlight = false;
      }
    };
    const timer = window.setInterval(() => void tick(), POLL_MS);
    // Back from the Telegram app (phone) or from Telegram Desktop: look at once, do not wait for the next beat.
    const wake = () => void tick();
    document.addEventListener("visibilitychange", wake);
    window.addEventListener("focus", wake);
    window.addEventListener("pageshow", wake);
    void tick();
    return () => {
      stopped = true;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", wake);
      window.removeEventListener("focus", wake);
      window.removeEventListener("pageshow", wake);
    };
  }, [req, t]);

  return { phase, begin, reset };
}

/**
 * The Telegram button and, after the click, the wait for the confirmation in its place. `onDone` signs in (or finishes
 * the step-up); its errors (a blocked account, a refused step-up) are shown under the button, which starts again.
 */
export function TelegramButton({ purpose = "sign_in", onDone }: { purpose?: Purpose; onDone: (d: TelegramDone) => Promise<unknown> }) {
  const t = useAuthT();
  const config = useTelegramConfig();
  const hintId = useId();
  const enabled = !!config?.configured && !!config.botUsername;
  const flow = useTelegramFlow(purpose, enabled, onDone);
  const { phase } = flow;

  if (phase.name === "waiting") return <TelegramWait req={phase.req} bot={config?.botUsername ?? ""} onCancel={flow.reset} />;

  const message = phase.name === "error" ? phase.message : phase.name === "expired" ? t("telegram.expired") : null;
  const label = enabled ? t("telegram.button") : t("telegram.notConfigured");
  const busy = phase.name === "opening";
  return (
    <div className="flex flex-col items-center gap-2">
      {/* A disabled <button> gets no hover in some browsers: aria-disabled keeps the tooltip and the focus. */}
      <button
        type="button"
        onClick={() => void flow.begin()}
        aria-disabled={!enabled || busy}
        aria-label={t("telegram.button")}
        aria-describedby={enabled ? undefined : hintId}
        title={label}
        className={cn(
          "inline-flex size-12 items-center justify-center rounded-full bg-[#229ED9] text-white shadow-sm transition-[transform,opacity,filter] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background",
          enabled && !busy ? "hover:brightness-110 active:scale-95" : "cursor-not-allowed opacity-45",
          busy && "animate-pulse",
        )}
      >
        <TelegramLogo className="size-6 -translate-x-px" />
      </button>
      {!enabled && (
        <span id={hintId} className="sr-only">
          {t("telegram.notConfigured")}
        </span>
      )}
      <p aria-live="polite" className={cn("flex min-h-4 items-center gap-1.5 text-center text-xs leading-ui", message ? "text-destructive" : "text-muted-foreground")}>
        {message && <CircleAlert aria-hidden className="size-3.5 shrink-0" />}
        {message ?? (busy ? t("telegram.opening") : "")}
      </p>
    </div>
  );
}

/** The wait, in place of the button: what to do in Telegram, the way back into it, and (computer) a QR for a phone. */
function TelegramWait({ req, bot, onCancel }: { req: Req; bot: string; onCancel: () => void }) {
  const t = useAuthT();
  const [phone] = useState(isPhone);
  const [qr, setQr] = useState<string | null>(null);
  useEffect(() => {
    if (!phone) void QRCode.toDataURL(req.botUrl, { margin: 1, width: 288 }).then(setQr, () => undefined);
  }, [phone, req.botUrl]);
  const steps = [t("telegram.stepOpen"), t("telegram.stepConfirm"), t("telegram.stepBack")];

  return (
    <section aria-live="polite" aria-labelledby="tg-wait-title" className="edge-glow flex flex-col gap-4 rounded-[14px] border border-border bg-background/40 p-4 sm:p-5">
      <header className="flex items-start gap-3">
        <span className="mt-0.5 inline-flex size-9 shrink-0 items-center justify-center rounded-full bg-[#229ED9] text-white">
          <TelegramLogo className="size-5 -translate-x-px" />
        </span>
        <div className="min-w-0">
          <h2 id="tg-wait-title" className="flex items-center gap-2 text-[15px] font-semibold leading-snug">
            {t("telegram.waitingTitle")}
            <LoaderCircle aria-hidden className="size-4 animate-spin text-emerald-600 motion-reduce:animate-none dark:text-[#D4FF24]" />
          </h2>
          <p className="mt-1 text-[13px] leading-relaxed text-muted-foreground">
            {req.purpose === "step_up" ? t("telegram.waitingHintStepUp", { bot }) : t("telegram.waitingHint", { bot })}
          </p>
        </div>
      </header>

      {/* The three steps, connected: where the person is in the flow (they do steps 1–2 in Telegram). */}
      <ol className="flex items-center gap-2 text-[12px] text-muted-foreground">
        {steps.map((s, i) => (
          <li key={s} className="flex min-w-0 flex-1 items-center gap-2">
            <span className={cn("inline-flex size-5 shrink-0 items-center justify-center rounded-full border text-[11px] tabular-nums", i === 1 ? "border-emerald-600 text-emerald-600 dark:border-[#D4FF24] dark:text-[#D4FF24]" : "border-border")}>{i + 1}</span>
            <span className="truncate">{s}</span>
            {i < steps.length - 1 && <span aria-hidden className="h-px min-w-2 flex-1 bg-border" />}
          </li>
        ))}
      </ol>

      {qr && (
        <div className="flex items-center gap-4">
          <img src={qr} alt="" width={112} height={112} className="size-28 shrink-0 rounded-[10px] bg-white p-1" />
          <div className="flex min-w-0 flex-col gap-2">
            <p className="text-[13px] leading-relaxed text-muted-foreground">{t("telegram.qr")}</p>
            <a href={req.botUrl} target="_blank" rel="noopener noreferrer" className="text-[13px] font-medium text-foreground underline underline-offset-4">
              {t("telegram.openWeb")}
            </a>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" className="flex-1" onClick={() => openTelegram(req, phone)}>
          {t("telegram.openAgain")}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel}>
          {t("telegram.cancel")}
        </Button>
      </div>
    </section>
  );
}

/** Telegram's paper plane (the official mark, simplified to one path), white on the brand-blue disc. */
function TelegramLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className={className} fill="currentColor">
      <path d="M21.94 4.3a1.1 1.1 0 0 0-1.5-1.23L2.86 9.85c-.97.38-.96 1.75.02 2.1l4.37 1.56 1.69 5.42c.2.66 1.04.86 1.52.37l2.43-2.48 4.62 3.4c.63.46 1.53.12 1.7-.65L21.94 4.3ZM9.3 13.2l8.37-6.07c.23-.17.5.15.29.35l-6.9 6.43-.27 3.06-1.49-3.77Z" />
    </svg>
  );
}

/** Divider "— or —" between the form and the Telegram button. */
export function OrDivider() {
  const t = useAuthT();
  return (
    <div className="flex items-center gap-3 text-xs uppercase tracking-[0.12em] text-muted-foreground" aria-hidden="true">
      <span className="h-px flex-1 bg-border" />
      {t("or")}
      <span className="h-px flex-1 bg-border" />
    </div>
  );
}
